import { Box, Grid, IconButton, ClickAwayListener, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Cancel } from '@mui/icons-material';
import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import { capitalize, toUpper } from 'lodash';
import moment from 'moment';
import { useFirestore } from 'react-redux-firebase';
import collections from '../../constants/FirestoreCollections';
import { useSelector } from 'react-redux';

const useStyles = (small) =>
  makeStyles((theme) => ({
    main: {
      backgroundColor: 'rgba(0,0,0,.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100%',
      zIndex: 1001,
    },
    statusHistoryBody: {
      backgroundColor: theme.palette.white.main,
      maxWidth: '500px',
      padding: 8,
      paddingBottom: 32,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
    },
    statusHistoryClose: {
      width: 30,
      height: 30,
    },
    statusItem: {
      paddingBlock: 16,
      display: 'flex',
      alignItems: 'center',
    },
    statusSection: {
      display: 'flex',
      justifyContent: 'center',
    },
    statusTag: {
      width: 'fit-content',
      borderRadius: '3rem',
      padding: '2px 16px',
      textAlign: 'center',
      fontSize: theme.sizes.sm.t1,
      color: 'white',
    },
    statusDate: {
      fontSize: theme.sizes.sm.t1,
    },
    body: {
      maxHeight: '300px',
      overflow: 'auto',
      '&::-webkit-scrollbar': {
        width: '1em',
      },
      '&::-webkit-scrollbar-track': {
        '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,.2)',
      },
    },
    chipBox: {
      textAlign: 'right',
      padding: '0 8px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    refChips: {
      fontSize: theme.sizes.sm.t1,
      marginBottom: 4,
    },
    chips: {
      textAlign: 'center',
      color: 'white',
      width: 'fit-content',
      minWidth: small ? '120px' : '100px',
      borderRadius: '3rem',
      cursor: 'pointer',
      height: '23px',
      paddingInline: 8,
      '&:hover': {
        opacity: '.8',
      },
      marginRight: 8,
      marginBottom: 8,
    },
    chip: {
      minWidth: small && '120px',
    },
    chipState: {
      margin: small && '4px 0',
    },
  }));

function StatusHistoryPopup(props) {
  const { handleClose, chat, chatId } = props;
  const history =
    chat && chat.statusHistory && chat.statusHistory.length > 0 ? chat.statusHistory : [];
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small)();
  const config = useSelector((state) => state.app.prestobotsConfig);
  const organization = useSelector((state) => state.app.organization);
  const profile = useSelector((state) => state.firebase.profile);
  const firestore = useFirestore();
  const auth = useSelector((state) => state.firebase.auth);

  const getStatusTag = (status) => {
    let statusInfo = config && config.states && config.states.find((e) => e.key === status);
    if (status === 3) {
      statusInfo = {
        label: 'terminado',
        color: '#4EC04E',
        key: 3,
      };
    }
    return (
      <Typography
        className={classes.statusTag}
        sx={{
          backgroundColor: (statusInfo && statusInfo.color) || '#ffcc01',
        }}
      >
        {statusInfo && capitalize(statusInfo.label)}
      </Typography>
    );
  };

  const handleChipClick = async (statusId) => {
    const updates = { status: statusId };
    if (statusId === 3) {
      updates.botStopped = false;
    }
    firestore
      .collection(collections.chats)
      .doc(chatId)
      .update(updates)
      .catch(() => {
        // TODO
      });

    const newStatus = {
      creationDate: firestore.Timestamp.fromDate(new Date()),
      status: statusId,
      user: auth.email,
    };
    firestore
      .collection(collections.chats)
      .doc(chatId)
      .update({
        statusHistory: firestore.FieldValue.arrayUnion(newStatus),
        status: statusId,
      })
      .catch(() => {
        // TODO
      });
    // TODO updateChatSupportAndAssignment({ chatId, chat }); Contabilizar los chats cuando estos cambian estados
  };

  const notificationsService =
    organization && organization.services && organization.services.notifications;

  const states = useMemo(() => {
    if (!organization || !organization.states || !profile) return [];

    const { configuration } = organization;
    const chatStatesConfig = configuration?.chatStates?.userAssociatedStates;
    const associatedStates = chatStatesConfig?.associatedStates || [];
    const isRestricted = chatStatesConfig?.enabled;
    const profileHasClaims = profile?.claims && profile.claims.length > 0;

    if (profileHasClaims) return [...organization.states, notificationsService && 6];

    if (!isRestricted) return [...organization.states, notificationsService && 6];

    return [
      ...organization.states.filter((state) => {
        const associatedState = associatedStates.find(
          ({ state: associatedState }) => associatedState.key === state,
        );
        if (associatedState) {
          return associatedState.users.some((user) => user.email === profile.email);
        }
        return true;
      }),
      notificationsService && 6,
    ];
  }, [organization]);

  return (
    <Box className={classes.main}>
      <ClickAwayListener onClickAway={handleClose}>
        <Grid container className={classes.statusHistoryBody}>
          <Box display="flex" justifyContent="space-between">
            <IconButton className={classes.statusHistoryClose} onClick={handleClose} size="large">
              <Cancel />
            </IconButton>
            <Box mr={2}>
              <img src="/logo512.png" width="30" alt="PrestoBots" />
            </Box>
          </Box>
          <Box mx={2} className={classes.body}>
            <Grid container>
              <Typography>
                <b>Historial de estados</b>
              </Typography>
              <Grid xs={12}>
                <Box my={2}>
                  <Typography className={classes.refChips}>Pasar a</Typography>
                  <Box className={classes.chipBox} m={0}>
                    {states
                      .filter(
                        (state) =>
                          config && config.states && config.states.find((e) => e.key === state),
                      )
                      .map(
                        (state) =>
                          config && config.states && config.states.find((e) => e.key === state),
                      )
                      .map((state) => (
                        <Box
                          key={state.key}
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          className={classes.chips}
                          sx={{
                            backgroundColor: state.color || '#ffcc01',
                          }}
                          onClick={async (event) => {
                            event.stopPropagation();
                            await handleChipClick(state.key);
                          }}
                        >
                          {state.label}
                        </Box>
                      ))}
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      className={classes.chips}
                      sx={{
                        backgroundColor: '#4EC04E',
                      }}
                      onClick={async (event) => {
                        event.stopPropagation();
                        await handleChipClick(3);
                      }}
                    >
                      terminado
                    </Box>
                  </Box>
                </Box>
              </Grid>
              <Grid xs={12} className={classes.statusItem} container>
                <Grid xs={8}>
                  <Box display="flex" flexDirection="column">
                    <Typography>Inicio</Typography>
                    <Typography className={classes.statusDate}>
                      {chat &&
                        chat.creationDate &&
                        moment(chat.creationDate.seconds * 1000).format('LLLL')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid xs={4} className={classes.statusSection}>
                  {getStatusTag(0)}
                </Grid>
              </Grid>
              {history.map((item, index) => (
                <Grid key={index} xs={12} className={classes.statusItem} container>
                  <Grid xs={8}>
                    <Box display="flex" flexDirection="column">
                      <Typography>
                        {item && item.user && toUpper(item.user) === 'BOT' ? 'BOT' : item.user}
                      </Typography>
                      <Typography className={classes.statusDate}>
                        {item &&
                          item.creationDate &&
                          item.creationDate.seconds &&
                          moment(item.creationDate.seconds * 1000).format('LLLL')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid xs={4} className={classes.statusSection}>
                    {getStatusTag(item.status)}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </ClickAwayListener>
    </Box>
  );
}

const Props = {
  handleClose: propTypes.func,
  chat: propTypes.object,
  chatId: propTypes.string,
};

StatusHistoryPopup.propTypes = Props;

export default StatusHistoryPopup;
